/**
 * Zentraler Meldungsweg fuers Frontend -- Gegenstueck zu
 * `backend/src/utils/protokoll.ts`.
 *
 * `log.warn`/`log.error` schreiben wie gewohnt auf die Browser-Konsole
 * (fuer die lokale Entwicklung unveraendert nuetzlich) UND melden
 * zusaetzlich an GlitchTip, sofern `initSentry()` einen DSN vorfand.
 * `log.info`/`log.debug` bleiben reine Konsolenausgabe -- dieselbe
 * Grenze wie im Backend: nur Warnung und Fehler sind ein Vorfall, den
 * jemand pruefen soll.
 *
 * Redaktion ist hier bewusst schlanker als im Backend: es gibt keinen
 * Anfragekontext und keine strukturierten Kopfzeilen zu filtern. Was
 * bleibt, ist dieselbe Grundregel -- Geheimnisse/PII gehen nicht als
 * Zusatzfeld raus. Freitext in der Fehlermeldung selbst faellt NICHT
 * darunter (dieselbe Grenze wie im Backend-Protokoll).
 */
import { Sentry, sentryAktiv } from '../config/sentry';

const GEHEIME_FELDER = [
  'authorization',
  'cookie',
  'passwort',
  'password',
  'token',
  'access_token',
  'refresh_token',
  'jwt',
  'code',
  'mfa_code',
  'secret',
  'geheimnis',
  'api_key',
  'apiKey',
  'schluessel',
  'email',
  'e_mail',
  'iban',
  'bic',
];

function istEinfachesObjekt(wert: unknown): wert is Record<string, unknown> {
  return (
    typeof wert === 'object' &&
    wert !== null &&
    !Array.isArray(wert) &&
    !(wert instanceof Error) &&
    Object.getPrototypeOf(wert) === Object.prototype
  );
}

function redigiert(felder: Record<string, unknown>): Record<string, unknown> {
  const ergebnis: Record<string, unknown> = {};
  for (const [schluessel, wert] of Object.entries(felder)) {
    if (GEHEIME_FELDER.includes(schluessel)) {
      ergebnis[schluessel] = '[redigiert]';
    } else if (istEinfachesObjekt(wert)) {
      const geschachtelt: Record<string, unknown> = {};
      for (const [k2, v2] of Object.entries(wert)) {
        geschachtelt[k2] = GEHEIME_FELDER.includes(k2) ? '[redigiert]' : v2;
      }
      ergebnis[schluessel] = geschachtelt;
    } else {
      ergebnis[schluessel] = wert;
    }
  }
  return ergebnis;
}

type Stufe = 'warn' | 'error';

function melde(stufe: Stufe, argumente: unknown[]): void {
  // eslint-disable-next-line no-console -- das ist der eine erlaubte Ort dafuer
  console[stufe](...argumente);

  if (!sentryAktiv()) return;

  try {
    const fehler = argumente.find((a): a is Error => a instanceof Error);
    const felder: Record<string, unknown> = {};
    const teile: string[] = [];
    for (const arg of argumente) {
      if (arg instanceof Error) continue;
      if (istEinfachesObjekt(arg)) Object.assign(felder, arg);
      else if (typeof arg === 'string') teile.push(arg);
      else {
        try {
          teile.push(JSON.stringify(arg));
        } catch {
          teile.push(String(arg));
        }
      }
    }
    const meldung = teile.join(' ').trim();
    const sentryStufe: Sentry.SeverityLevel = stufe === 'warn' ? 'warning' : 'error';
    const zusatz = redigiert(felder);

    Sentry.withScope((scope) => {
      scope.setLevel(sentryStufe);
      for (const [schluessel, wert] of Object.entries(zusatz)) {
        scope.setExtra(schluessel, wert);
      }
      if (fehler) {
        Sentry.captureException(fehler);
      } else {
        Sentry.captureMessage(meldung || `(${stufe} ohne Text)`, sentryStufe);
      }
    });
  } catch {
    // Ein Meldungsweg, der selbst wirft, darf die eigentliche Anweisung
    // nicht zum Scheitern bringen.
  }
}

export const log = {
  debug: (...a: unknown[]): void => {
    // eslint-disable-next-line no-console
    console.debug(...a);
  },
  info: (...a: unknown[]): void => {
    // eslint-disable-next-line no-console
    console.info(...a);
  },
  warn: (...a: unknown[]): void => melde('warn', a),
  error: (...a: unknown[]): void => melde('error', a),
};

export default log;
